import { Search, ArrowUpDown, X } from "lucide-react";
import { Input } from "@/components/ui/input";

/** Filter / sort bar shared by admin tabs.
 *  - q: free-text search (name / email)
 *  - date: YYYY-MM-DD optional filter
 *  - sort: one of: date_desc, date_asc, name_asc, name_desc
 */
export default function TableControls({ q, setQ, date, setDate, sort, setSort, sortOptions, dataTestPrefix = "" }) {
    const opts = sortOptions || [
        { value: "date_desc", label: "Date · newest" },
        { value: "date_asc", label: "Date · oldest" },
        { value: "name_asc", label: "Name · A → Z" },
        { value: "name_desc", label: "Name · Z → A" },
    ];
    return (
        <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-col md:flex-row gap-3 mt-6">
            <div className="relative flex-1 min-w-[200px]">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.75} />
                <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by name or email"
                    data-testid={`${dataTestPrefix}search-input`}
                    className="pl-9 h-10 bg-slate-50 border-slate-200"
                />
                {q && (
                    <button
                        onClick={() => setQ("")}
                        data-testid={`${dataTestPrefix}search-clear`}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                        aria-label="Clear search"
                    >
                        <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </button>
                )}
            </div>
            {setDate && (
                <div className="relative md:w-44">
                    <Input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        data-testid={`${dataTestPrefix}date-input`}
                        className="h-10 bg-slate-50 border-slate-200"
                    />
                    {date && (
                        <button
                            onClick={() => setDate("")}
                            data-testid={`${dataTestPrefix}date-clear`}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                            aria-label="Clear date"
                        >
                            <X className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                    )}
                </div>
            )}
            <div className="md:w-56 relative">
                <ArrowUpDown className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" strokeWidth={1.75} />
                <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    data-testid={`${dataTestPrefix}sort-select`}
                    className="h-10 w-full bg-slate-50 border border-slate-200 rounded-md text-sm pl-9 pr-3 focus:outline-none focus:ring-1 focus:ring-brand"
                >
                    {opts.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>
        </div>
    );
}

/** Compose a generic comparator for sort=`<key>_<dir>` */
export function applySort(items, sort, getName, getDate) {
    const [key, dir] = sort.split("_");
    const sign = dir === "asc" ? 1 : -1;
    return [...items].sort((a, b) => {
        if (key === "name") {
            const an = (getName(a) || "").toLowerCase();
            const bn = (getName(b) || "").toLowerCase();
            if (an < bn) return -1 * sign;
            if (an > bn) return 1 * sign;
            return 0;
        }
        const ad = getDate(a) || "";
        const bd = getDate(b) || "";
        if (ad < bd) return -1 * sign;
        if (ad > bd) return 1 * sign;
        return 0;
    });
}

/** Filter by free-text q (matches name + email) and optional date string */
export function applyFilters(items, q, date, getName, getEmail, getDate) {
    const ql = (q || "").trim().toLowerCase();
    return items.filter((it) => {
        if (ql) {
            const n = (getName(it) || "").toLowerCase();
            const e = ((getEmail ? getEmail(it) : "") || "").toLowerCase();
            if (!n.includes(ql) && !e.includes(ql)) return false;
        }
        if (date) {
            const d = getDate(it) || "";
            if (!d.startsWith(date)) return false;
        }
        return true;
    });
}
